<template>
  <div class="rounded-lg overflow-hidden relative">
    <GoogleMap :api-key="mapsKey" class="w-full" style="height: 500px" :center="mapCenter" :zoom="8" mapId="ContactMap">
      <MarkerCluster>
        <AdvancedMarker 
          v-for="(contact, index) in contactsWithPosition" 
          :key="index" 
          :options="{ position: contact.position }"
          :pin-options="{ background: '#FBBC04' }"
        >
          <InfoWindow>
            <div>
              <div id="siteNotice" />
              <h1 id="firstHeading" class="firstHeading text-xl font-bold">{{ contact.name }}</h1>
              <div id="bodyContent">
                <p v-if="contact.phone">
                  <span class="font-semibold">Phone: </span>{{ contact.phone }}
                </p>
                <p v-if="contact.email">
                  <span class="font-semibold">Email: </span>{{ contact.email }}
                </p>
                <p v-if="contact.organization">
                  <span class="font-semibold">Organization: </span>{{ contact.organization.name }}
                </p>
                <p v-if="contact.type">
                  <span class="font-semibold">Type: </span>{{ contact.type }}
                </p>
                <p v-if="contact.address || contact.city || contact.state || contact.postal_code">
                  <span class="font-semibold">Address: </span>
                </p>
                <p>
                  <span v-if="contact.address">{{ contact.address }}</span>                
                </p>
                <p>
                  <span v-if="contact.city">{{ contact.city }}</span>
                  <span v-if="contact.state">, {{ contact.state }}</span>
                  <span v-if="contact.postal_code">, {{ contact.postal_code }}</span>
                </p>
                <p>
                  <Link :href="`/contacts/${contact.id}/edit`" class="text-blue-500 hover:text-blue-700 font-bold">Click to Open Contact</Link>
                </p>
              </div>
            </div>
          </InfoWindow>
        </AdvancedMarker>
      </MarkerCluster>
    </GoogleMap>
    <a 
      @click="updateUserLocation" 
      class="absolute bottom-2 right-2 text-blue-500 hover:text-blue-700 cursor-pointer text-sm"
    >
      Center on my location
    </a>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, watch } from 'vue'
import { GoogleMap, AdvancedMarker, MarkerCluster, InfoWindow } from 'vue3-google-map'
import { Link } from '@inertiajs/vue3'

export default defineComponent({
  components: { GoogleMap, AdvancedMarker, MarkerCluster, InfoWindow, Link },
  props: {
    contacts: {
      type: Object,
      required: true
    },
  },
  setup(props) {
    const mapsKey = 'AIzaSyDQquNc_B-5tx0eFkTxWGpGlYJCm_QqbKw'
    const contactsWithPosition = ref([])
    const defaultCenter = { lat: 42.732535, lng: -84.5555347 }
    const mapCenter = ref(defaultCenter)

    const getGeolocation = async (contact) => {
      const cachedPosition = getCachedPosition(contact)
      if (cachedPosition) {
        return { 
          ...contact, 
          position: {
            lat: Number(cachedPosition.lat),
            lng: Number(cachedPosition.lng)
          }
        }
      }

      if (!contact.city || !contact.state) {
        return null
      }

      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            `${contact.address}, ${contact.city}, ${contact.state || ''}`
          )}&key=${mapsKey}`
        )
        const data = await response.json()
        if (data.results && data.results.length > 0) {
          const location = data.results[0].geometry.location
          const position = {
            lat: Number(location.lat),
            lng: Number(location.lng)
          }
          cachePosition(contact, position)
          return { ...contact, position }
        }
      } catch (error) {
        console.error('Error fetching geolocation data:', error)
      }
      return null
    }

    const getCachedPosition = (contact) => {
      const cachedData = localStorage.getItem(`contact_${contact.id}_position`)
      return cachedData ? JSON.parse(cachedData) : null
    }

    const cachePosition = (contact, position) => {
      localStorage.setItem(`contact_${contact.id}_position`, JSON.stringify(position))
    }

    const fetchGeolocation = async () => {
      const contactsWithPositionArr = await Promise.all(
        props.contacts.data.map(async (contact) => {
          const position = await getGeolocation(contact)
          return position
        })
      )

      contactsWithPosition.value = contactsWithPositionArr.filter(contact => contact !== null)
    }

    const updateUserLocation = () => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userPosition = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            }
            mapCenter.value = userPosition
          },
          (error) => {
            console.error('Error getting user location:', error)
          }
        )
      } else {
        console.error('Geolocation is not supported by this browser.');
      }
    }

    onMounted(() => {
      fetchGeolocation()
    })

    watch(() => props.contacts, (newContacts) => {
      console.log('Contacts changed:', newContacts)
      fetchGeolocation()
    }, { deep: true })

    return { mapsKey, contactsWithPosition, mapCenter, updateUserLocation }
  },
})
</script>